<template>
  <div class="hike-trails-panel" :class="{ collapsed: isCollapsed }">
    <div class="panel-header">
      <button class="collapse-btn" @click="toggleCollapse" :title="isCollapsed ? 'Show trails' : 'Hide trails'">
        <span class="material-icons">{{ isCollapsed ? 'left_panel_open' : 'left_panel_close' }}</span>
      </button>
      <h3 class="panel-title">{{ hikeName || 'Hike days' }}</h3>
      <button v-if="plannedTrails && plannedTrails.length > 1" class="toggle-btn" @click="toggleFullTrail" :class="{ active: activeView === 'full' }" title="View full trail">
        <img :src="activeView === 'full' ? focusRouteOnIcon : focusRouteOffIcon" alt="Full trail" />
      </button>
      <div
        v-if="activeView === 'full' && plannedTrails && plannedTrails.length > 1"
        class="trail-actions-wrapper"
        data-menu-key="full"
      >
        <button class="options-btn" @click.stop="showFullTrailOptions($event)" title="Trail options">
          <span class="material-icons">more_vert</span>
        </button>
        <div
          v-if="openMenuKey === 'full'"
          class="trail-actions-menu"
          :style="menuStyle"
          @click.stop
        >
          <button
            v-for="item in actionMenuItems"
            :key="`full-${item.key}`"
            class="trail-actions-menu-item"
            type="button"
            @click="handleFullTrailAction(item.key)"
          >
            <span class="material-icons">{{ item.icon }}</span>
            <span class="trail-actions-menu-text">{{ item.label }}</span>
          </button>
        </div>
      </div>
      <!-- Options button for single route (desktop only) -->
      <div
        v-if="plannedTrails && plannedTrails.length === 1"
        class="trail-actions-wrapper single-route-options"
        data-menu-key="single"
      >
        <button class="options-btn" @click.stop="showSingleRouteOptions($event)" title="Trail options">
          <span class="material-icons">more_vert</span>
        </button>
        <div
          v-if="openMenuKey === 'single'"
          class="trail-actions-menu"
          :style="menuStyle"
          @click.stop
        >
          <button
            v-for="item in actionMenuItems"
            :key="`single-${item.key}`"
            class="trail-actions-menu-item"
            type="button"
            @click="handleFullTrailAction(item.key)"
          >
            <span class="material-icons">{{ item.icon }}</span>
            <span class="trail-actions-menu-text">{{ item.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="panel-content" v-show="!isCollapsed">
      <div class="trail-list" v-if="plannedTrails && plannedTrails.length > 1">
        <!-- Show days if there are multiple trails with day info -->
        <template v-if="showDays">
          <div v-for="day in daysToShow" :key="`day-${day}`" class="day-section">
            <h4 class="day-header">
              <span class="day-title" @click="toggleDay(day)" style="cursor: pointer;">
                {{ getDayName(day) }}
                <small v-if="getDayDescription(day)" class="day-description">{{ getDayDescription(day) }}</small>
              </span>
              <button class="day-toggle-btn" @click="toggleDay(day)" :class="{ active: activeView === `day-${day}` }">
                <img :src="activeView === `day-${day}` ? focusRouteOnIcon : focusRouteOffIcon" alt="Toggle day" />
              </button>
              <div
                v-if="activeView === `day-${day}`"
                class="trail-actions-wrapper"
                :data-menu-key="`day:${day}`"
              >
                <button class="day-options-btn" @click.stop="showDayOptions(day, $event)" title="Day options">
                  <span class="material-icons">more_vert</span>
                </button>
                <div
                  v-if="openMenuKey === `day:${day}`"
                  class="trail-actions-menu"
                  :style="menuStyle"
                  @click.stop
                >
                  <button
                    v-for="item in actionMenuItems"
                    :key="`day-${day}-${item.key}`"
                    class="trail-actions-menu-item"
                    type="button"
                    @click="handleDayAction(item.key, day)"
                  >
                    <span class="material-icons">{{ item.icon }}</span>
                    <span class="trail-actions-menu-text">{{ item.label }}</span>
                  </button>
                </div>
              </div>
            </h4>
            <div
              v-for="trail in getTrailsForDay(day)"
              :key="trail.id"
              class="trail-item"
              :class="{ active: selectedTrailId === getTrailId(trail) }"
              @click="selectTrail(trail)"
            >
              <div class="trail-info">
                <span class="route-style-label" :class="`route-style-label--${getTrailRouteStyle(trail)}`">
                  {{ getTrailRouteStyle(trail).toUpperCase() }}
                </span>
                <h4 class="trail-title">{{ getTrailTitle(trail) }}</h4>
                <div class="trail-stats">
                  <span>{{ formatDistance(getTrailDistance(trail)) }}</span>
                  <span class="separator">·</span>
                  <span>{{ formatElevation(getTrailElevation(trail)) }}</span>
                  <span class="separator">·</span>
                  <span>{{ formatDuration(getTrailDuration(trail)) }}</span>
                </div>
              </div>
              <button class="route-toggle-btn" @click.stop="toggleRoute(trail)" :class="{ active: activeView === `route-${getTrailId(trail)}` }">
                <img :src="activeView === `route-${getTrailId(trail)}` ? focusRouteOnIcon : focusRouteOffIcon" alt="Toggle route" />
              </button>
              <div
                v-if="activeView === `route-${getTrailId(trail)}`"
                class="trail-actions-wrapper"
                :data-menu-key="`route:${getTrailId(trail)}`"
              >
                <button class="trail-actions" @click.stop="showTrailActions(trail, $event)" title="Route options">
                  <span class="material-icons">more_vert</span>
                </button>
                <div
                  v-if="openMenuKey === `route:${getTrailId(trail)}`"
                  class="trail-actions-menu"
                  :style="menuStyle"
                  @click.stop
                >
                  <button
                    v-for="item in actionMenuItems"
                    :key="`route-${getTrailId(trail)}-${item.key}`"
                    class="trail-actions-menu-item"
                    type="button"
                    @click="handleTrailAction(item.key, trail)"
                  >
                    <span class="material-icons">{{ item.icon }}</span>
                    <span class="trail-actions-menu-text">{{ item.label }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Show simple list if no day info or single trail -->
        <template v-else>
          <div
            v-for="(trail, index) in sortedTrails"
            :key="trail.id"
            class="trail-item"
            :class="{ active: selectedTrailId === getTrailId(trail) }"
            @click="selectTrail(trail)"
          >
            <div class="trail-info">
              <span class="route-style-label" :class="`route-style-label--${getTrailRouteStyle(trail)}`">
                {{ getTrailRouteStyle(trail).toUpperCase() }}
              </span>
              <h4 class="trail-title">{{ getTrailTitle(trail) }}</h4>
              <div class="trail-stats">
                <span>{{ formatDistance(getTrailDistance(trail)) }}</span>
                <span class="separator">·</span>
                <span>{{ formatElevation(getTrailElevation(trail)) }}</span>
                <span class="separator">·</span>
                <span>{{ formatDuration(getTrailDuration(trail)) }}</span>
              </div>
            </div>
            <button class="route-toggle-btn" @click.stop="toggleRoute(trail)" :class="{ active: activeView === `route-${getTrailId(trail)}` }">
              <img :src="activeView === `route-${getTrailId(trail)}` ? focusRouteOnIcon : focusRouteOffIcon" alt="Toggle route" />
            </button>
            <div
              v-if="activeView === `route-${getTrailId(trail)}`"
              class="trail-actions-wrapper"
              :data-menu-key="`route:${getTrailId(trail)}`"
            >
              <button class="trail-actions" @click.stop="showTrailActions(trail, $event)" title="Route options">
                <span class="material-icons">more_vert</span>
              </button>
              <div
                v-if="openMenuKey === `route:${getTrailId(trail)}`"
                class="trail-actions-menu"
                :style="menuStyle"
                @click.stop
              >
                <button
                  v-for="item in actionMenuItems"
                  :key="`route-${getTrailId(trail)}-${item.key}`"
                  class="trail-actions-menu-item"
                  type="button"
                  @click="handleTrailAction(item.key, trail)"
                >
                  <span class="material-icons">{{ item.icon }}</span>
                  <span class="trail-actions-menu-text">{{ item.label }}</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div v-if="!plannedTrails || plannedTrails.length === 0" class="no-trails-message">
        <p>No planned trails available for this hike.</p>
      </div>

      <div class="panel-footer">
        <div class="total-stats">
          <div class="total-label">Total</div>
          <div class="total-values">
            <strong>{{ formatDistance(totalDistance) }}</strong>
            <span class="separator">·</span>
            <strong>{{ formatElevation(totalElevation) }}</strong>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import offIcon from "../../images/focus_route_off.svg";
import onIcon from "../../images/focus_route_on.svg";
import graphClient from '../../utils/graphql/graph_client';
import { SEND_TO_GARMIN } from '../../utils/graphql/send-to-garmin';
import { createGarminConnector } from '../../utils/helpers/connectGarminHelper';
import type { GarminConnector } from '../../utils/helpers/connectGarminHelper';

const TRAIL_ACTION_ITEMS = [
  { key: 'print', icon: 'print', label: 'Print Map' },
  { key: 'edit', icon: 'edit', label: 'Edit copy in hike planner' },
  { key: 'directions', icon: 'directions_car', label: 'Driving Directions' },
  { key: 'gpx', icon: 'file_download', label: 'Export GPX' },
  { key: 'gps-device', icon: 'gps_fixed', label: 'Send to Garmin' }
] as const;

export default defineComponent({
  name: 'HikeTrailsPanel',
  props: {
    hikeName: {
      type: String,
      default: ''
    },
    plannedTrails: {
      type: Array,
      default: () => []
    },
    hikeDays: {
      type: Array,
      default: () => []
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    activeViewProp: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isCollapsed: this.collapsed,
      selectedTrailId: null as string | null,
      activeView: this.activeViewProp?.type === 'route' ? `route-${this.activeViewProp.trailId}` :
                  this.activeViewProp?.type === 'day' ? `day-${this.activeViewProp.dayNumber}` :
                  'full' as string,
      focusRouteOnIcon: onIcon,
      focusRouteOffIcon: offIcon,
      openMenuKey: null as string | null,
      garminConnector: createGarminConnector() as GarminConnector,
      isSendingToGarmin: false,
      isConnectingToGarmin: false,
      menuPosition: { top: 0, left: 0 } as { top: number, left: number }
    };
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    if (this.garminConnector) {
      this.garminConnector.cancel();
    }
  },
  computed: {
    // Check if the data structure has day information
    hasJoinTableData(): boolean {
      if (this.plannedTrails.length === 0) return false;
      const firstTrail = this.plannedTrails[0];
      const hasJoinData = firstTrail && 
             'day' in firstTrail &&
             'ptv2PlannedTrail' in firstTrail;
      console.log('hasJoinTableData check:', {
        firstTrail,
        hasJoinData,
        hasDayProp: firstTrail && 'day' in firstTrail,
        hasPtv2Prop: firstTrail && 'ptv2PlannedTrail' in firstTrail
      });
      return hasJoinData;
    },
    
    // Should show days if multiple trails and they have day info
    showDays(): boolean {
      if (!this.hasJoinTableData) {
        console.log('showDays: false (no join table data)');
        return false;
      }
      const result = this.plannedTrails.length > 1 && 
             this.plannedTrails.some((t: any) => t.day != null);
      console.log('showDays:', result, 'trails with days:', this.plannedTrails.filter((t: any) => t.day != null));
      return result;
    },
    
    // Get sorted trails - works with both data structures
    sortedTrails(): any[] {
      const trails = [...this.plannedTrails];
      const sorted = trails.sort((a, b) => {
        const posA = this.hasJoinTableData ? (a.position || 0) : this.plannedTrails.indexOf(a);
        const posB = this.hasJoinTableData ? (b.position || 0) : this.plannedTrails.indexOf(b);
        return posA - posB;
      });
      console.log('sortedTrails:', sorted);
      return sorted;
    },
    
    // Get days to show
    daysToShow(): number[] {
      if (!this.showDays) return [];
      
      const days = new Set<number>();
      this.plannedTrails.forEach((trail: any) => {
        if (trail.day != null) {
          days.add(trail.day);
        }
      });
      
      const result = Array.from(days).sort((a, b) => a - b);
      console.log('daysToShow:', result);
      return result;
    },
    
    totalDistance() {
      return this.plannedTrails.reduce((sum: number, trail: any) => {
        const distance = this.getTrailDistance(trail);
        return sum + (distance || 0);
      }, 0);
    },
    
    totalElevation() {
      return this.plannedTrails.reduce((sum: number, trail: any) => {
        const elevation = this.getTrailElevation(trail);
        return sum + (elevation || 0);
      }, 0);
    },

    actionMenuItems() {
      return TRAIL_ACTION_ITEMS;
    },

    menuStyle(): Record<string, string> {
      const { top, left } = this.menuPosition;
      return {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: '1200'
      };
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit('update:collapsed', this.isCollapsed);
      if (this.isCollapsed) {
        this.closeMenus();
      }
    },

    selectTrail(trail: any) {
      this.setActiveRoute(trail);
      // On mobile, also trigger the focus/zoom behavior and close panel
      if (window.innerWidth <= 768) {
        const trailId = this.getTrailId(trail);
        this.activeView = `route-${trailId}`;
        this.$emit('view-changed', { type: 'route', trailId });
        // Close the mobile panel to show the map
        this.$emit('update:collapsed', true);
      }
    },
    
    // Helper methods to work with both data structures
    getTrailId(trail: any): string | number {
      if (this.hasJoinTableData) {
        const id = trail.ptv2PlannedTrail?.id || trail.id;
        return id;
      }
      return trail.id;
    },
    
    getTrailTitle(trail: any): string {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.title || `Route ${trail.position}`;
      }
      return trail.title || `Route ${this.plannedTrails.indexOf(trail) + 1}`;
    },
    
    getTrailDistance(trail: any): number {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.distanceMeters || 0;
      }
      return trail.distanceMeters || 0;
    },
    
    getTrailElevation(trail: any): number {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.elevationGainMeters || 0;
      }
      return trail.elevationGainMeters || 0;
    },
    
    getTrailDuration(trail: any): number {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.calculatedDurationMinutes || 0;
      }
      return trail.calculatedDurationMinutes || 0;
    },
    
    getTrailRouteStyle(trail: any): string {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.routeStyle || 'route';
      }
      return 'route';
    },

    getTrailsForDay(day: number): any[] {
      return this.sortedTrails.filter(trail => trail.day === day);
    },

    formatDistance(distanceMeters: number) {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.bigUnitFormatter(distanceMeters / 1000, true);
    },

    formatElevation(elevationMeters: number) {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.smallUnitFormatter(elevationMeters, false);
    },

    formatDuration(minutes: number) {
      if (!minutes) return '0h';
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours === 0) return `${mins}m`;
      if (mins === 0) return `${hours}h`;
      return `${hours}h ${mins}m`;
    },

    showTrailActions(trail: any, evt?: MouseEvent) {
      const trailId = this.getTrailId(trail);
      const key = `route:${trailId}`;
      const isOpening = this.openMenuKey !== key;
      this.openMenuKey = isOpening ? key : null;
      if (isOpening) {
        this.updateMenuPositionFromEvent(evt);
        this.$emit('show-options', { type: 'route', data: trail });
      }
    },

    closeMenus() {
      this.openMenuKey = null;
    },

    handleDocumentClick(event: MouseEvent) {
      if (this.openMenuKey == null) return;

      const target = event.target as HTMLElement | null;
      if (!target) return;

      const wrapper = target.closest('[data-menu-key]');
      if (wrapper && wrapper.getAttribute('data-menu-key') === this.openMenuKey) {
        return;
      }

      this.closeMenus();
    },

    handleTrailAction(action: string, trail: any) {
      this.executeTrailAction(action, trail);
      this.$emit('show-options', { type: 'route-action', action, data: trail });
      this.closeMenus();
    },

    handleFullTrailAction(action: string) {
      this.executeTrailAction(action);
      this.$emit('show-options', { type: 'full-action', action, data: null });
      this.closeMenus();
    },

    executeTrailAction(action: string, trail?: any) {
      switch (action) {
        case 'print':
          this.printMap(trail);
          break;
        case 'edit':
          this.editInHikePlanner(trail);
          break;
        case 'directions':
          this.openDrivingDirections(trail);
          break;
        case 'gpx':
          this.downloadGPX(trail);
          break;
        case 'gps-device':
          this.sendToGarmin(trail);
          break;
        default:
          console.warn('Unhandled trail action:', action, trail);
      }
    },

    editInHikePlanner(trail: any) {
      // This will be handled by the parent component
      // The parent has access to the full geojson data needed
    },

    openDrivingDirections(trail: any) {
      console.log('Driving directions for trail:', trail);
    },

    showFullTrailOptions(evt?: MouseEvent) {
      const key = 'full';
      const isOpening = this.openMenuKey !== key;
      this.openMenuKey = isOpening ? key : null;
      if (isOpening) {
        this.updateMenuPositionFromEvent(evt);
        this.$emit('show-options', { type: 'full', data: null });
      }
    },

    showSingleRouteOptions(evt?: MouseEvent) {
      const key = 'single';
      const isOpening = this.openMenuKey !== key;
      this.openMenuKey = isOpening ? key : null;
      if (isOpening) {
        this.updateMenuPositionFromEvent(evt);
        this.$emit('show-options', { type: 'full', data: null });
      }
    },
    
    showDayOptions(dayNumber: number, evt?: MouseEvent) {
      const key = `day:${dayNumber}`;
      const isOpening = this.openMenuKey !== key;
      this.openMenuKey = isOpening ? key : null;
      const dayTrails = this.getTrailsForDay(dayNumber);
      if (isOpening) {
        this.updateMenuPositionFromEvent(evt);
        this.$emit('show-options', { type: 'day', data: { dayNumber, trails: dayTrails } });
      }
    },

    updateMenuPositionFromEvent(evt?: MouseEvent) {
      const trigger = (evt?.currentTarget as HTMLElement) || null;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const gap = 6;
      const menuWidth = 220; // matches CSS min-width
      const menuHeight = 200; // approximate height of menu
      const baseOffset = window.innerWidth <= 576 ? 200 : 100;

      // Check if there's enough space below the trigger
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top: number;
      // On mobile or when not enough space below, open upward
      if (window.innerWidth <= 576 || spaceBelow < menuHeight) {
        // Position above the trigger
        top = rect.top - menuHeight - gap;
        // Ensure it doesn't go off the top of the screen
        top = Math.max(top, 8);
      } else {
        // Position below the trigger
        top = rect.bottom + gap - baseOffset;
      }

      let left = rect.right - menuWidth;

      const viewportPadding = 8;
      const maxLeft = window.innerWidth - menuWidth - viewportPadding;
      left = Math.min(Math.max(left, viewportPadding), Math.max(maxLeft, viewportPadding));

      this.menuPosition = { top, left };
    },

    handleDayAction(action: string, dayNumber: number) {
      const dayData = { dayNumber, trails: this.getTrailsForDay(dayNumber) };
      this.executeTrailAction(action, dayData);
      this.$emit('show-options', { type: 'day-action', action, data: dayData });
      this.closeMenus();
    },

    copyToHikes() {
      // TODO: Implement copy functionality
      console.log('Copy to my hikes');
    },

    downloadGPX(trail?: any) {
      // Placeholder for parent listeners
    },

    inferTrailForAction(trail?: any): any | null {
      if (trail) {
        if (Array.isArray(trail.trails) && trail.trails.length > 0) {
          return trail.trails[0];
        }
        return trail;
      }

      const activeView: string = this.activeView || '';

      if (activeView === 'full') {
        return null; // Prefer global fallback when sending entire hike
      }

      if (activeView.startsWith('route-')) {
        const id = activeView.replace('route-', '');
        return this.findTrailById(id);
      }

      if (activeView.startsWith('day-')) {
        const day = Number(activeView.replace('day-', ''));
        if (!Number.isNaN(day)) {
          const byDay = this.getTrailsForDay(day);
          if (byDay.length > 0) {
            return byDay[0];
          }
        }
      }

      return this.sortedTrails.length > 0 ? this.sortedTrails[0] : null;
    },

    findTrailById(trailId: string | number): any | null {
      const targetId = String(trailId);
      return this.sortedTrails.find((candidate: any) => String(this.getTrailId(candidate)) === targetId) || null;
    },

    convertTrailToTarget(trail: any): { klass: string; id: string | number } | null {
      if (!trail) {
        return null;
      }

      if (this.hasJoinTableData && trail.ptv2PlannedTrail?.id != null) {
        return { klass: 'Ptv2::PlannedTrail', id: trail.ptv2PlannedTrail.id };
      }

      if (trail.trailId != null) {
        return { klass: 'Trail', id: trail.trailId };
      }

      if (trail.trail?.id != null) {
        return { klass: 'Trail', id: trail.trail.id };
      }

      if (trail.recordingId != null) {
        return { klass: 'Recording', id: trail.recordingId };
      }

      if (trail.recording?.id != null) {
        return { klass: 'Recording', id: trail.recording.id };
      }

      if (trail.type && trail.id != null) {
        return { klass: trail.type, id: trail.id };
      }

      if (trail.__typename && trail.id != null) {
        return { klass: trail.__typename, id: trail.id };
      }

      if (!this.hasJoinTableData && trail.id != null) {
        return { klass: 'Ptv2::PlannedTrail', id: trail.id };
      }

      return null;
    },

    resolveHikeDayTarget(dayNumber: number | null | undefined): { klass: string; id: string | number } | null {
      if (dayNumber == null) {
        return null;
      }

      const numericDay = Number(dayNumber);
      if (Number.isNaN(numericDay)) {
        return null;
      }

      const match = (this.hikeDays || []).find((day: any) => Number(day.dayNumber) === numericDay);
      if (match?.id != null) {
        return { klass: 'HikeDay', id: match.id };
      }

      return null;
    },

    resolveGarminTargets(context?: any): Array<{ klass: string; id: string | number }> {
      const targets: Array<{ klass: string; id: string | number }> = [];

      const hikeDayTarget = this.resolveHikeDayTarget(context?.dayNumber);
      if (hikeDayTarget) {
        targets.push(hikeDayTarget);
      } else if (context?.trails && Array.isArray(context.trails)) {
        context.trails.forEach((trail: any) => {
          const target = this.convertTrailToTarget(trail);
          if (target) {
            targets.push(target);
          }
        });
      } else {
        const singleTarget = this.resolveGarminTarget(context, true);
        if (singleTarget) {
          targets.push(singleTarget);
        }
      }

      const uniqueTargets = new Map<string, { klass: string; id: string | number }>();
      targets.forEach((target) => {
        const key = `${target.klass}:${target.id}`;
        if (!uniqueTargets.has(key)) {
          uniqueTargets.set(key, target);
        }
      });

      return Array.from(uniqueTargets.values());
    },

    resolveGarminTarget(trail?: any, skipCollection = false): { klass: string; id: string | number } | null {
      if (!skipCollection && trail?.trails && Array.isArray(trail.trails)) {
        for (const dayTrail of trail.trails) {
          const target = this.convertTrailToTarget(dayTrail);
          if (target) {
            return target;
          }
        }
      }

      const chosenTrail = this.inferTrailForAction(trail);

      const converted = this.convertTrailToTarget(chosenTrail);
      if (converted) {
        return converted;
      }

      const fallbackId = (window as any).hike_id || (window as any).id || (window as any).trailId;
      if (fallbackId != null) {
        return { klass: 'Hike', id: String(fallbackId) };
      }

      return null;
    },

    async sendToGarmin(trail?: any) {
      if (this.isSendingToGarmin || this.isConnectingToGarmin) {
        return;
      }

      const targets = this.resolveGarminTargets(trail);
      if (!targets.length) {
        alert('We could not determine which trail to send. Please try again.');
        return;
      }

      const user = (window as any).user;
      if (!user) {
        alert('Please sign in to send trails to Garmin.');
        return;
      }

      const sendToGarminRequest = async () => {
        try {
          this.isSendingToGarmin = true;
          for (const target of targets) {
            const response = await graphClient.request(SEND_TO_GARMIN, {
              klass: target.klass,
              id: String(target.id)
            });

            if (!response.sendToGarmin) {
              throw new Error('Failed to send trail to Garmin. Please try again.');
            }
          }

          const successMessage = targets.length > 1
            ? 'Routes successfully sent to your Garmin device! They will appear in Garmin Connect shortly.'
            : 'Hike successfully sent to your Garmin device! It will appear in Garmin Connect shortly.';

          alert(successMessage);
        } catch (error: any) {
          console.error('Error sending to Garmin:', error);
          alert(error?.response?.errors?.[0]?.message || 'Failed to send trail to Garmin. Please try again.');
        } finally {
          this.isSendingToGarmin = false;
        }
      };

      if (!user.garmin_connected) {
        try {
          this.isConnectingToGarmin = true;
          await this.garminConnector.connect();
          user.garmin_connected = true;
        } catch (error: any) {
          const message = error?.message || 'Failed to connect to Garmin. Please try again.';
          alert(message);
          return;
        } finally {
          this.isConnectingToGarmin = false;
        }
      }

      await sendToGarminRequest();
    },

    printMap(trail?: any) {
      // Handled by parent component via emitted event
    },
    
    getDayName(dayNumber: number): string {
      const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === dayNumber);
      return dayInfo?.name || `Day ${dayNumber}`;
    },
    
    getDayDescription(dayNumber: number): string {
      const dayInfo = this.hikeDays.find((d: any) => d.dayNumber === dayNumber);
      return dayInfo?.description || '';
    },

    toggleFullTrail() {
      this.activeView = 'full';
      this.selectedTrailId = null;
      this.closeMenus();
      this.$emit('view-changed', { type: 'full' });
      // On mobile, close the panel to show the map
      if (window.innerWidth <= 768) {
        this.$emit('update:collapsed', true);
      }
    },

    toggleDay(dayNumber: number) {
      this.activeView = `day-${dayNumber}`;
      this.selectedTrailId = null;
      this.closeMenus();
      this.$emit('view-changed', { type: 'day', dayNumber });
      // On mobile, close the panel to show the map
      if (window.innerWidth <= 768) {
        this.$emit('update:collapsed', true);
      }
    },

    toggleRoute(trail: any) {
      this.setActiveRoute(trail);
    },

    setActiveRoute(trail: any) {
      const trailId = this.getTrailId(trail);
      this.selectedTrailId = trailId;
      this.activeView = `route-${trailId}`;
      this.closeMenus();

      const plannedTrail = this.hasJoinTableData ? trail.ptv2PlannedTrail : trail;
      const index = this.sortedTrails.indexOf(trail);
      this.$emit('trail-selected', plannedTrail, index);
      this.$emit('view-changed', { type: 'route', trailId });
    }
  }
});
</script>

<style scoped>
.hike-trails-panel {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 300px;
  max-height: 46vh;
  background: rgba(40, 42, 50, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  color: #fff;
  border-radius: 12px;
}

.hike-trails-panel.collapsed {
  width: auto;
  max-height: 48px;
}

.hike-trails-panel.collapsed .panel-content {
  display: none;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(30, 32, 38, 0.8);
  min-height: 48px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.panel-title {
  flex: 1;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toggle-btn,
.day-toggle-btn,
.route-toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  width: 24px;
  height: 24px;
}

.toggle-btn img,
.day-toggle-btn img,
.route-toggle-btn img {
  width: 16px;
  height: 16px;
}

.toggle-btn:hover,
.day-toggle-btn:hover,
.route-toggle-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.toggle-btn.active,
.day-toggle-btn.active,
.route-toggle-btn.active {
  background-color: rgba(255, 223, 70, 0.2);
}

.options-btn,
.day-options-btn,
.trail-actions {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  margin-left: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.options-btn:hover,
.day-options-btn:hover,
.trail-actions:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.options-btn .material-icons,
.day-options-btn .material-icons,
.trail-actions .material-icons {
  font-size: 18px;
}

.collapse-btn,
.close-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.collapse-btn:hover,
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}


.panel-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.trail-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.day-section {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.45);
}

.day-header {
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}

.day-title {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.day-header small.day-description {
  display: block;
  font-size: 0.78rem;
  font-weight: normal;
  text-transform: none;
  color: rgba(255, 255, 255, 0.55);
  font-style: italic;
}

.trail-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.75rem;
  cursor: pointer;
  background: #24252d;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
  transition: background 0.2s, border-color 0.2s;
  position: relative;
}

.trail-item:hover {
  background: #2b2c35;
}

.trail-item.active {
  background: #2f303a;
  border-color: #fddf00;
  box-shadow: 0 0 0 1px rgba(253, 223, 0, 0.3), 0 8px 20px rgba(0, 0, 0, 0.45);
}

.route-style-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  font-size: 0.55rem;
  font-weight: 600;
  letter-spacing: 0.8px;
  padding: 0.1rem 0.55rem;
  border-radius: 999px;
  border: 1px solid transparent;
  width: fit-content;
  white-space: nowrap;
  margin-bottom: .5rem;
}

.route-style-label--route {
  color: #ff8e7a;
  border-color: rgba(255, 65, 54, 0.35);
  background: rgba(255, 65, 54, 0.08);
}

.route-style-label--spur {
  color: #f7a8ff;
  border-color: rgba(230, 0, 255, 0.35);
  background: rgba(230, 0, 255, 0.1);
}

.route-style-label--alt {
  color: #8ceaff;
  border-color: rgba(0, 207, 232, 0.35);
  background: rgba(0, 207, 232, 0.1);
}

.route-style-label--transport {
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.25);
  background-image: repeating-linear-gradient(
    90deg,
    transparent,
    transparent 3px,
    rgba(255, 255, 255, 0.1) 3px,
    rgba(255, 255, 255, 0.1) 6px
  );
}

.trail-actions-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.trail-actions-menu {
  position: fixed;
  min-width: 200px;
  background: rgba(30, 32, 38, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
  padding: 6px 0;
  z-index: 9999;
  backdrop-filter: blur(8px);
}

.trail-actions-menu-item {
  width: 100%;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  text-align: left;
}

.trail-actions-menu-item:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.trail-actions-menu-item .material-icons {
  font-size: 18px;
}

.trail-actions-menu-text {
  white-space: nowrap;
}

.trail-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.trail-title {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.trail-stats {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.separator {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.65rem;
}


.panel-actions {
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: none;
  color: rgba(255, 255, 255, 0.8);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  justify-content: center;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.action-btn .material-icons {
  font-size: 16px;
}

.action-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 380px) {
  .action-text {
    display: none;
  }

  .action-btn {
    flex: 0 0 auto;
    padding: 8px;
  }
}

.panel-footer {
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(30, 32, 38, 0.8);
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

.total-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.total-values {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
}

.total-values strong {
  font-weight: 600;
  color: #fff;
}

/* Handle collapsed state */
.hike-trails-panel.collapsed .panel-header {
  border-bottom: none;
}

.hike-trails-panel.collapsed .toggle-btn {
  display: none;
}

.hike-trails-panel.collapsed .panel-title {
  font-size: 13px;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .hike-trails-panel {
    width: 280px;
    left: 10px;
  }

  /* Hide single route options on mobile */
  .single-route-options {
    display: none;
  }
}

@media (max-width: 480px) {
  .hike-trails-panel {
    width: 100%;
    max-width: none;
    left: 0;
    right: 0;
    bottom: 0;
    top: unset;
    /* height: 520px; */
    max-height: 420px;
    border-radius: 0;
    z-index: 1010;
  }
}

/* Scrollbar styling */
.trail-list::-webkit-scrollbar {
  width: 6px;
}

.trail-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.trail-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.trail-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* No trails message */
.no-trails-message {
  padding: 32px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

.no-trails-message p {
  margin: 0;
}
</style>
